import { algorithm, outcomes } from "../util/algorithm";
import {
  parseCode,
  parseSearch,
  randNonce,
  generateCodeChallenge,
  exchangeCodeForToken,
} from "../util/helper";
import { useCookies } from "react-cookie";
import BuyMeACoffee from "./BuyMeACoffee";
import CancellationToken from "../util/CancellationToken";
import Help from "./Help";
import Hero from "./Hero";
import InfoCard from "./InfoCard";
import React, { useEffect, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";

// TODO refactor this into multiple components/functions to be more testable

const oauthStateCookie = "oauth-state";
const codeVerifierCookie = "oauth-code-verifier";
const cancelToken = new CancellationToken();

function Shuffler() {
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [cookies, setCookie, removeCookie] = useCookies([
    oauthStateCookie,
    codeVerifierCookie,
  ]);

  // Run once when app loads to handle the redirect back from Spotify
  useEffect(() => {
    let isValid, state, code, error;

    // Handle a successful authorization (Authorization Code flow with PKCE).
    // Spotify redirects back with "?code=...&state=..." which we exchange for an
    // access token, sending the stored PKCE code verifier instead of a secret.
    [isValid, state, code] = parseCode(window.location.search);
    if (isValid) {
      const expectedState = cookies[oauthStateCookie];
      const codeVerifier = cookies[codeVerifierCookie];
      // Clean the query string out of the URL so a refresh doesn't re-run this.
      window.history.pushState("", document.title, window.location.pathname);

      if (!expectedState || expectedState !== state) {
        // State mismatch, possible security issue, show error
        setLoadingText("Something went wrong.");
        setAccessToken("");
        return;
      }
      if (!codeVerifier) {
        setLoadingText("Something went wrong. Please try logging in again.");
        setAccessToken("");
        return;
      }

      setLoadingText("Logging you in...");
      exchangeCodeForToken(process.env.REACT_APP_TOKEN_ENDPOINT, {
        code,
        codeVerifier,
        clientId: process.env.REACT_APP_CLIENT_ID,
        redirectUri: process.env.REACT_APP_REDIRECT_URI,
      }).then(([ok, token]) => {
        removeCookie(codeVerifierCookie, { path: "/" });
        if (ok) {
          setAccessToken(token);
          setLoadingText("");
        } else {
          setLoadingText("Something went wrong logging in. Please try again.");
          setAccessToken("");
        }
      });
      return;
    }

    // Handle a failed authorization (e.g. the user denied access)
    [isValid, state, error] = parseSearch(window.location.search);
    if (isValid && cookies[oauthStateCookie] === state) {
      setLoadingText(error);
      window.history.pushState("", document.title, window.location.pathname);
    } else if (isValid && cookies[oauthStateCookie] !== state) {
      // State mismatch, possible security issue, show error
      setLoadingText("Something went wrong.");
      setAccessToken("");
      window.history.pushState("", document.title, window.location.pathname);
    }
  }, [cookies, removeCookie]);

  // Kick off the Authorization Code with PKCE login flow
  const handleLogin = async () => {
    const state = randNonce(20);
    const codeVerifier = randNonce(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Persist the state and code verifier so we can validate and complete the
    // token exchange after Spotify redirects back.
    setCookie(oauthStateCookie, state, { path: "/", maxAge: 3600 });
    setCookie(codeVerifierCookie, codeVerifier, { path: "/", maxAge: 3600 });

    const loginUrl =
      process.env.REACT_APP_AUTH_ENDPOINT +
      "?client_id=" +
      process.env.REACT_APP_CLIENT_ID +
      "&response_type=code" +
      "&redirect_uri=" +
      process.env.REACT_APP_REDIRECT_URI +
      "&state=" +
      state +
      "&scope=" +
      process.env.REACT_APP_SCOPES +
      "&code_challenge_method=S256" +
      "&code_challenge=" +
      codeChallenge;
    window.location.href = loginUrl;
  };

  const handleShuffleQueue = async () => {
    // Make sure there is an internet connection
    if (!navigator.onLine) {
      setLoadingText("An internet connection is required. Try again later.");
      return;
    }
    setIsLoading(true);
    setLoadingText("Shuffling queue. Your music will be briefly interrupted.");
    let { outcome, count } = await algorithm(
      new SpotifyWebApi(),
      accessToken,
      cancelToken
    );
    switch (outcome) {
      case outcomes.UNAUTHENTICATED: {
        setAccessToken("");
        setLoadingText("Please login again.");
        break;
      }
      case outcomes.ERROR: {
        setLoadingText(
          "Something went wrong on Spotify's end. Please try again."
        );
        break;
      }
      case outcomes.SUCCESS: {
        if (count === 0) {
          setLoadingText(`No songs in your queue to shuffle.`);
        } else {
          // TODO show how long it took to shuffle the queue?
          setLoadingText(`Shuffled your queue of ${count} songs.`);
        }
        break;
      }
      case outcomes.NOTHING_PLAYING: {
        setLoadingText(
          "Nothing is playing right now. Start by opening Spotify and playing a song."
        );
        break;
      }
      case outcomes.CANCELLED: {
        setLoadingText(
          "Because you cancelled songs may be missing from your queue."
        );
        break;
      }
      case outcomes.NOT_PREMIUM: {
        setLoadingText(
          "You must have a premium Spotify account for this to work."
        );
        break;
      }
      default: {
        setLoadingText("Something went wrong. Please reload the page.");
      }
    }
    setIsLoading(false);
    cancelToken.reset();
  };

  // Build primary button
  const buttonText = accessToken ? "Shuffle Your Queue" : "Login With Spotify";
  const primaryCss =
    "font-body font-semibold w-64 flex flex-row justify-around items-center py-2 bg-spotifygreen text-white text-xl rounded-full px-4 py-1 select-none focus:outline-none " +
    (isLoading ? "opacity-50" : "shadow-xl");
  let primaryButton = null;
  if (!accessToken) {
    primaryButton = (
      <button className={primaryCss} onClick={handleLogin}>
        <img
          src="/Spotify.png"
          width="30"
          height="30"
          alt=""
          className="inline"
        />
        {buttonText}
      </button>
    );
  } else {
    primaryButton = (
      <button className={primaryCss} onClick={handleShuffleQueue}>
        {buttonText}
      </button>
    );
  }

  // Build secondary button
  const secondaryCss =
    "text-red-400 font-body underline py-8 select-none focus:outline-none " +
    (isLoading ? "block" : "invisible");
  let secondaryButton = (
    <button
      className={secondaryCss}
      onClick={() => {
        let msg =
          "Are you sure you would like to stop shuffling? Songs may disappear from your queue.";
        if (window.confirm(msg)) {
          cancelToken.cancel();
        }
      }}
    >
      Stop
    </button>
  );

  return (
    <div className="max-w-xl w-full h-full sm:h-auto flex flex-col justify-end items-center">
      <div className="w-full flex-grow">
        <Hero isLoading={isLoading} isAuthenticated={accessToken !== ""} />
      </div>
      <InfoCard text={loadingText} />
      <div className="flex flex-col justify-start items-center">
        {primaryButton}
        {secondaryButton}
      </div>
      <div className="w-full flex flex-row justify-between sm:justify-center items-center">
        <Help />
        <BuyMeACoffee />
      </div>
    </div>
  );
}

export default Shuffler;
